import { Box, Flex, FormControl, IconButton, Image, Switch, Table, Tbody, Td, Th, Thead, Tr, Text } from '@chakra-ui/react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { formatDate } from 'utils/helpers';
import { FORMAT_DATE, ROOT_API, SystemNotiOptions } from 'constants/common';
import { usechangePublishNotiMutation } from 'services/notification';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { toast } from 'components/Toast';
import { useHistory } from 'react-router-dom';
import { useDeleteSystemNotificationSetting } from 'services/system-notification';
import { formatImageUrl } from 'utils/url';
const SystemNotificationTable = ({ notificationsData, refetch }) => {
  const [sorting, setSorting] = useState([]);
  const columnHelper = createColumnHelper();
  const changePublishNotiMutation = usechangePublishNotiMutation();
  const deleteNotification = useDeleteSystemNotificationSetting();
  const history = useHistory();

  const handleSwitchChange = noti => {
    changePublishNotiMutation.mutate(
      { notificationId: noti?._id },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Đã thay đổi trạng thái xuất bản thông báo');
          refetch?.();
        },
        onError: () => {
          toast.showMessageError('Thay đổi trạng thái xuất bản thông báo thất bại');
          refetch?.();
        },
      }
    );
  };

  const handleDeleteNoti = noti => {
    const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa thông báo này không?');
    if (!confirmDelete) {
      return;
    }

    deleteNotification.mutate(
      { notificationId: noti?._id },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Xóa thông báo thành công');
          refetch?.();
        },
        onError: () => {
          toast.showMessageError('Xóa thông báo thất bại');
          refetch?.();
        },
      }
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Tên',
        cell: info => <Box textTransform="capitalize">{info.renderValue()}</Box>,
      }),
      columnHelper.accessor('content', {
        header: 'Nội dung',
        cell: info => (
          <Text noOfLines={3} letterSpacing={0.5}>
            {info.renderValue()}
          </Text>
        ),
      }),
      columnHelper.accessor('type', {
        header: 'Thể loại',
        cell: info =>
          SystemNotiOptions.map((key, value) => {
            if (info?.row?.original?.type == key.value) {
              return <Box key={key}>{key.label}</Box>;
            }
          }),
      }),
      columnHelper.accessor('actionPulished', {
        header: 'Thao tác trạng thái phát hành',
        cell: info => (
          <FormControl display="flex" alignItems="center">
            <Switch
              disabled={!!info?.row?.original?.isPublished}
              defaultChecked={info?.row?.original?.isPublished}
              onChange={() => handleSwitchChange(info.row.original)}
            />
          </FormControl>
        ),
      }),
      columnHelper.accessor('isPublished', {
        header: 'Trạng thái phát hành',
        cell: info => (info.renderValue() ? 'Đã xuất bản' : 'Bản nháp'),
      }),
      columnHelper.accessor('avatar', {
        header: 'Ảnh đại diện',
        cell: info =>
          info.renderValue() ? <Image src={formatImageUrl(info.renderValue())} boxSize="50px" mb={4} /> : <Box boxSize="50px" />,
      }),
      columnHelper.accessor('createdAt', {
        header: 'Ngày tạo',
        cell: info => formatDate(info.getValue(), FORMAT_DATE),
      }),
      columnHelper.accessor('action', {
        header: 'Thao tác',
        cell: info => (
          <Flex alignItems="center" gap={1}>
            <IconButton
              bg="transparent"
              onClick={() => {
                history.push(`/admin/system-noti/create?_id=${info?.row?.original?._id}`);
              }}
            >
              <EditIcon cursor="pointer" boxSize={4} />
            </IconButton>

            <IconButton
              bg="transparent"
              onClick={() => {
                handleDeleteNoti(info?.row?.original);
              }}
            >
              <DeleteIcon cursor="pointer" boxSize={4} />
            </IconButton>
          </Flex>
        ),
      }),
    ],
    [notificationsData]
  );

  const table = useReactTable({
    data: notificationsData || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Table>
      <Thead>
        {table.getHeaderGroups().map(headerGroup => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <Th key={header.id} minW="180px">
                {header.isPlaceholder ? null : (
                  <Box cursor={header.column.getCanSort() ? 'pointer' : 'default'} onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: ' 🔼',
                      desc: ' 🔽',
                    }[header.column.getIsSorted()] ?? null}
                  </Box>
                )}
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody overflow={'scroll'}>
        {isEmpty(table.getRowModel().rows) ? (
          <Tr>
            <Td textAlign="center" colSpan={10}>
              Không có dữ liệu
            </Td>
          </Tr>
        ) : (
          table.getRowModel().rows.map(row => (
            <Tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
              ))}
            </Tr>
          ))
        )}
      </Tbody>
    </Table>
  );
};

export default SystemNotificationTable;
